import React, { useEffect, useState } from 'react';
import getDemoRequestsApi from '../../networking/apis/getDemoRequests';
import updateDemoRequestApi from '../../networking/apis/updateDemoRequest';
import './listingPage.css';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css'; // Optional styling
import {followCursor} from 'tippy.js';

interface DataItem {
  id: number;
  name: string;
  email: string;
  companyname: string;
  message: string;
  createdat: string;
  isapproved: boolean | undefined;
  isrejected: boolean | undefined;
}
interface ListingPageStateType {
    showAlert: boolean;
    alertMessage: string;
    alertType: 'warning' | 'confirmation';
}

const ListingPage: React.FC<{ token: string | null ; adminEmail: string | null ; onLogout: () => void }> = ({ token, adminEmail, onLogout }) => {
  const [data, setData] = useState<DataItem[]>([]);
  const [filteredData, setFilteredData] = useState<DataItem[]>([]);
  const [filter, setFilter] = useState<string>('');
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [alertType, setAlertType] = useState<string>('');

  // Function to sort by createdAt descending
  const sortByCreatedAtDesc = (items: DataItem[]) => {
    return items.sort((a, b) => new Date(b.createdat).getTime() - new Date(a.createdat).getTime());
  };

  // Modal state
  const [showModal, setShowModal] = useState<boolean>(false);
  const [rejectReason, setRejectReason] = useState<string>("");
  const [currentRejectItem, setCurrentRejectItem] = useState<{ email: string; companyname: string } | null>(null);

  // fetch and sort data
  useEffect(() => {
    const fetchData = async () => {
      const response = await getDemoRequestsApi(token)
      const sortedData = sortByCreatedAtDesc(response.data); // Sort by createdAt descending
      setData(sortedData);
      setFilteredData(sortedData);
    };
    fetchData();
  }, [token]);
  
  const handleRejectClick = (email: string, companyname: string) => {
    setCurrentRejectItem({ email, companyname });
    setShowModal(true);
  };

  const alert = (message: string, type: ListingPageStateType["alertType"] = 'warning') => {
    setAlertMessage(message)
    setAlertType(type)
    setShowAlert(true)
    
    setTimeout(() => {
      setAlertMessage('')
      setAlertType('')
      setShowAlert(false)
    }, 2000); 
  };

  //approval button click
  const handleApprove = async (email: string, companyname: string) => {
    const approveResponse = await updateDemoRequestApi({ action: 'approve', email, companyname, token, adminEmail })
    if(approveResponse?.status) {
      const updatedData = data.map((item) =>
        item.email === email ? { ...item, isapproved: true } : item
      );
      const sortedData = sortByCreatedAtDesc(updatedData); // Sort after updating
      setData(sortedData); // Update the full data
      setFilteredData(sortedData); // Update the filtered data as well
      alert('Approved Successfully', 'confirmation')
    } else {
      alert('Update Failed')
    }
  };

  // reject button click
  const handleReject = async () => {
    if (!currentRejectItem) return;

    const rejectResponse = await updateDemoRequestApi({
      action: 'reject',
      email: currentRejectItem.email,
      companyname: currentRejectItem.companyname,
      token,
      adminEmail,
      reason: rejectReason
    })

    if(rejectResponse?.status) {
      const updatedData = data.map((item) =>
        item.email === currentRejectItem.email ? { ...item, isrejected: true } : item
      );
      const sortedData = sortByCreatedAtDesc(updatedData); // Sort after updating
      setData(sortedData); // Update the full data
      setFilteredData(sortedData); // Update the filtered data as well
      setShowModal(false);
      setRejectReason("");
      alert('Rejected Successfully', 'confirmation')
    } else {
      alert('Update Failed')
    }
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase();
    setFilter(value);
    setFilteredData(
      data.filter(
        (item) =>
          item.name.toLowerCase().includes(value) ||
          item.companyname.toLowerCase().includes(value) ||
          item.email.toLowerCase().includes(value)
      )
    );
  };

  return (
    <div>
      {/* Logout Button */}
      <button onClick={onLogout} className="logout-btn">
        Logout
      </button><div className="listing-page-container">

      <h1>Advance Demo Requests</h1>
      <input
        type="text"
        placeholder="Filter by name, company, email..."
        value={filter}
        onChange={handleFilterChange}
        className="filter-input"
      />

      <table className="data-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Company Name</th>
            <th>Message</th>
            <th>Created at</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredData.map((item) => (
            <tr key={item.id}>
              <td>{item.name}</td>
              <td>{item.email}</td>
              <td>{item.companyname}</td>
              <td>
                <Tippy content={item.message} followCursor={true} plugins={[followCursor]}>
                  <span>{item.message}</span>
                </Tippy>
              </td>
              <td>{item.createdat}</td>
              <td>
                { !item.isrejected ? <button onClick={() => handleApprove(item.email, item.companyname)} className="approve-btn" disabled={item.isapproved} >{item.isapproved ? "Approved" :"Approve"}</button> : <></>}
                { !item.isapproved ? <button onClick={() => handleRejectClick(item.email, item.companyname)} className="reject-btn" disabled={item.isrejected} >{item.isrejected ? "Rejected" :"Reject"}</button> : <></>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Reject Request</h3>
            <textarea
              placeholder="Enter the reason for rejection.."
              defaultValue="Not Authorised for Demo"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={2}
            />
            <div className="modal-actions">
              <button onClick={handleReject} className="approve-btn">
                Submit
              </button>
              <button onClick={() => setShowModal(false)} className="reject-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showAlert && (
          <div className={`alert-popup ${showAlert ? 'show' : ''} `}>
              <div className={`alert-inner ${alertType}`}>
                  <p>{alertMessage}</p>
              </div>
          </div>
      )}
      </div>
    </div>
  );
};

export default ListingPage;